import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { take } from 'rxjs/operators';
import { MainFormBase } from '../../../../../components/common/main-form-base/main-form-base';
import { PolluxFormConfig } from '../../../../../components/common/pollux-form-config/pollux-form-config';
import { WorkspaceBase } from '../../../../../components/common/workspace-base/workspace-base';
import { ProjectsWorkspaceComponent } from '../../workspaces/projects-workspace/projects-workspace.component';
import { projectFormConfig } from './projects-form-config';
import { SortHelper } from '../../../../../components/common/sort-helper/sort-helper';
import { environment } from '../../../../../../environments/environment';

/**
 * Projects form
 */
@Component({
  selector: 'projects-form',
  templateUrl: './projects-form.component.html',
  styleUrls: ['./projects-form.component.css']
})
export class ProjectsFormComponent extends MainFormBase<any> implements OnInit, OnDestroy {
  /**
   * The currency
   */
  public currency: string = null;

  /**
   * The unit of length
   */
  public unitOfLength: string = null;

  /**
   * A list of RACI templates
   */
  public raciTemplates: any[] = null;

  /**
   * Show the move to the previous item button
   */
  @Input() showPrevBtn = false;

  /**
   * Show the move to the next item button
   */
  @Input() showNextBtn = false;

  /**
   * Emits an event if the move to previous item button was clicked
   */
  @Output() showPrevious = new EventEmitter<void>();

  /**
   * Emits an event if the move to next item button was clicked
   */
  @Output() showNext = new EventEmitter<void>();

  /**
   * config_user reflist results with filtering applied for the project coordinator
   */
  public config_user_projectCoordinator: any[] = null;

  /**
   * config_user reflist results with filtering applied for the client development lead
   */
  public config_user_clientBusinessDevelopmentLead: any[] = null;

  /**
   * Sets the OID
   */
  @Input() set oid(value: number) {
    this.currentOid = value;
  }

  /**
   * The workspace
   */
  @Input() workspace: ProjectsWorkspaceComponent;

  /**
   * The required retrievers
   */
  protected get requiredRetrievers(): string[] {
    return [];
  }

  /**
   * The required ref lists
   */
  protected get requiredReflists(): string[] {
    return [
      'ref_recordstatus',
      'ref_projectphasevaluation',
      'ref_l2_acquiredvia',
      'ref_l2_valuationpurpose',
      'ref_l2_projectstd_internalconfidentiality',
      'ref_l2_liability_type',
      'ref_l2_projectstd_valuationmethod',
      'ref_l2_projectstd_valuationlimit',
      'ref_l2_projectstd_reportscope',
      'ref_l2_projectstd_basicagreement',
      'config_user',
      'config_business_units',
      'ref_unit_of_length',
      'ref_import_configuration',
      'ref_currency_with_code',
      'ref_l3_display_values_over_time'
    ];
  }

  /**
  * Constructor
  * @param injector The Angular injector
  */
  constructor(protected injector: Injector) {
    super(injector);
  }

  /**
  * Gets the API POST endpoint for saving a single BO
  */
  protected getSaveSingleEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `Projects`);
  }

  /**
  * Gets the API GET endpoint for loading a single BO by it's OID
  * @param oid The BO's OID
  */
  protected getLoadSingleEndpoint(oid: number): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `Projects?oid=${oid}`);
  }

  /**
  * Gets the API GET endpoint for loading RACI templates
  */
  protected getRaciTemplateListEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.settings.api', 'racitemplate/list/nousage');
  }

  /**
   * Gets the workspace reference
   */
  public get workspaceReference(): WorkspaceBase {
    return this.workspace;
  }

  /**
   * Closes the form
   */
  public close(): void {
    this.eventBus.broadcastEvent('closeProjectstdForm');
  }

  /**
   * Fets the form config
   */
  protected get formConfig(): PolluxFormConfig[] {
    return projectFormConfig;
  }

  /**
   * Performs post-processing of the data
   * Important: Please do not parse any Dates from string in this method!
   * @param data Loaded data
   */
  protected postProcessData(data: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      try {
        this.raciTemplates = this.additionalLoadPromisesResult.raciTemplatesList;

        this.reflistData.ref_currency_with_code.forEach(currency => {
          /*
           * currency.additionalDescription => €
           * currency.description => EUR - Euro
           * we need to display (in cuurency field labels): € - Euro
           */
          const parts = currency.description.split(" - ");

          currency.currencyName = parts.length > 1
            ? currency.additionalDescription + " - " + parts[1]
            : currency.additionalDescription + " - " + currency.description;
        });

        if (data.RaciTemplateId === null) {
          if (this.raciTemplates.find(t => t.status === 0)) {
            data.RaciTemplateId = this.raciTemplates.find(t => t.status === 0).oid;
          } else {
            data.RaciTemplateId = null;
          }
        }
        if (!environment.showProcessTemplateSettings) {
          //Task 15396: Process template / MVP - hide the function in settings
          this.raciTemplates = this.raciTemplates.filter(item => item.oid === 0);
        }
        data.SelectedRecurrentValuationEventIds = [];

        //Bug 15937: Removed user in PIX-WF still appears in selection lists | UAT: leave inactive only if already set (using this bug that inactive can be selected). additionalDescription contains the active flag
        this.config_user_projectCoordinator = this.reflistData.config_user.filter(u => u.additionalDescription || u.oid === 0 || u.oid === data.projectCoordinatorId);
        this.config_user_clientBusinessDevelopmentLead = this.reflistData.config_user.filter(u => u.additionalDescription || u.oid === 0 || u.oid === data.clientBusinessDevelopmentLeadId);
        this.sortDropdownData(this.config_user_clientBusinessDevelopmentLead);
        this.sortDropdownData(this.config_user_projectCoordinator);
        resolve(data);
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * If the save call was triggered, this method is called.
   * Use it to inform the user, close the form or trigger grid reloading.
   * @param saveResult Th result returned from the API
   */
  protected handlePostSave(saveResult: any): void {
    this.eventBus.broadcastEvent('projectsDataSaved', saveResult, this.currentOid === -1);
    this.close();
  }

  /**
   * If an error occurred during saving, this method is called.
   * Use it to re-enable buttons, inform the user, etc.
   * Note: This occurs on errors and doesn't evaluate the saveResult returned from the API, since this can be different between calls.
   * @param error The error information
   */
  protected handleSaveError(error: any): void {
    alert('An error occurred while saving your data!');
    console.error(error);
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * If the save process was aborted, this methd is called.
   * You can use it to re-enable buttons, inform the user, etc
   * @param _data The data that should be saved originally
   */
  public handleSaveAborted(_data: any): void {
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * Perform any pre-save activities here, for example user interaction, user queries etc.
   * If the Promise is resolved with true, saving will continue. If iit's resolved with false or rejected, saving is cancelled.
   * @param _data The data that should be saved
   */
  public handlePreSave(_data: any): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (this.isNew) {
        this.eventBus.broadcastEvent("openConfirmDialog", {
          customMessage: () => {
            return `The project will be set-up using the currency ${this.currency} and unit of length set-up to ${this.unitOfLength}.
                    This can't be changed once the project contains imported data. Do you want to continue?`;
          },
          success: () => {
            resolve(true);
          },
          cancel: () => {
            resolve(false);
          }
        }, 'saveProjectCurrency');
      }
      else {
        resolve(true);
      }
    });
  }

  /**
   * If a validation error occurs, this ethd is called.
   * Use it to inform the user, enable buttons, etc
   * @param _data The data
   */
  public handleValidationError(_data: any): void {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      hideConfirmButton: true,
      cancel: () => {
        //Nothing
      }
    }, 'fillRequiredFieldsDialog');
  }

  /**
   * Handles a currency change
   */
  public onCurrencyChanged(): void {
    this.currency = this.reflistData.ref_currency_with_code.find(c => c.oid == this.formData.controls.currencyId.value).currencyName;
  }

  /**
   * Handles a unit of length change
   */
  public onUnitOfLengthChanged(): void {
    this.unitOfLength = this.reflistData.ref_unit_of_length.find(unit => unit.oid == this.formData.controls.unitOfLengthId.value).description;
  }

  /**
   * The dataset status was updated
   * @param newRecordStatusId
   */
  public datasetStatusUpdated(newRecordStatusId): void {
    const formReadOnly = newRecordStatusId == 2;

    this.setReadOnly(formReadOnly);
  }

  /**
   * Destroys the component
   */
  public ngOnDestroy(): void {
    this.eventBus.broadcastEvent('hideParticipantsIcon', null);
    super.ngOnDestroy();
  }

  /**
   * Initializes the component
   */
  public ngOnInit(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('saveProjectData', () => {
      this.save();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('cancelProjectDataChanges', () => {
      this.close();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('valuationTabSelect', () => {
      this.close();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('projectMainTabSelect', (_tab, form) => {
      if (form !== true) {
        this.close();
      }
    }));

    const raciTemplatesUrl = this.getRaciTemplateListEndpoint();

    this.getObservableOnLoad('raciTemplatesList', this.httpClient.get(raciTemplatesUrl).pipe(take(1)));

    this.init().then(() => {
      this.eventBus.broadcastEvent('hideParticipantsIcon', this.isNew);
      this.onCurrencyChanged();
      this.onUnitOfLengthChanged();

      this.eventBus.broadcastEvent('showSaveCancelNavigationAction', true);


      //inform infobar if required
      this.eventBus.broadcastEvent('project-valuation-infobar-item-changed', {
        mode: 'projectstd',
        item: this.formData.getRawValue(),
        itemIdentityProperty: 'Oid'
      });

      this.datasetStatusUpdated(this.formData.controls['RecordStatusId'].value);
    });
  }

  /**
   * Sets the default retriever data
   * @param _data
   */
  protected setDefaultRetrieverData(_data: any): void {
    //Nothing
  }

  /**
   * Next button clicked
   */
  public next(): void {
    this.showNext.emit();
  }

  /**
   * Previous button clicked
   */
  public previous(): void {
    this.showPrevious.emit();
  }

  /**
   * Evaluates the save result. Returns true if saving was successful
   * @param result The save result
   */
  public evaluateSaveResult(result: any): boolean {
    return result?.m_Item1;
  }

  /**
 * Allows processing / filtering / sorting of reflists
 * @param reflistName The reflist name
 * @param reflistData The reflist data
 * @returns The filtered reflist data
 */
  protected processReflistResult(reflistName: string, reflistData: any[]): any[] {
    if (reflistName === 'ref_projectphasevaluation') {
      reflistData.sort(SortHelper.sortArrayBy('description'));
    }
    if (reflistName === 'ref_l2_projectstd_valuationmethod' || reflistName === 'ref_l2_projectstd_valuationlimit') {
      return reflistData.filter(data => data.oid !== 0) //do not show empty item
    }
    return reflistData;
  }

  /**
   * Sorts dropdown data by description
   * @param array
   */
  sortDropdownData(array: any) {
    array.sort((a, b) => {
      const descriptionA = a.description.toLowerCase();
      const descriptionB = b.description.toLowerCase();
      return descriptionA.localeCompare(descriptionB);
    });
  }
}
